import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { X, Check } from "lucide-react";
import { LOGO_URL } from "@/lib/site-config";
import { useT } from "@/i18n/LangProvider";
import { useHomeHref } from "@/i18n/routing";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";
import SEO from "@/components/site/SEO";
import "@/styles/quote.css";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LETTERS = ["A", "B", "C", "D"];

// Step schema: (type, key, backend value template)
// - Steps 1-4 & 6 are choice steps (indices 0..3, 5)
// - Step 5 (index 4) is a free-text reference
// - Step 7 (index 6) collects name + email
const STEP_META = [
  { key: "diagnostic", type: "choice", stepKey: "steps.0" },
  { key: "business_type", type: "choice", stepKey: "steps.1" },
  { key: "objective", type: "choice", stepKey: "steps.2" },
  { key: "urgency", type: "choice", stepKey: "steps.3" },
  { key: "reference", type: "text", stepKey: "steps.4" },
  { key: "budget_range", type: "choice", stepKey: "steps.5" },
  { key: "contact", type: "contact", stepKey: "steps.6" },
];

const TOTAL = STEP_META.length;
const KNOWN_LANGS = ["de", "pt", "en"];

export default function QuoteForm() {
  const { t, setLang, lang } = useT();
  const { lang: urlLang } = useParams();
  const homeHref = useHomeHref();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({
    diagnostic: "",
    business_type: "",
    objective: "",
    urgency: "",
    reference: "",
    budget_range: "",
    name: "",
    email: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const meta = STEP_META[stepIdx];
  const step = t(`quote.${meta.stepKey}`) || {};

  useEffect(() => {
    if (urlLang && KNOWN_LANGS.includes(urlLang) && urlLang !== lang) {
      setLang(urlLang);
    }
  }, [urlLang, lang, setLang]);

  useEffect(() => {
    const to = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 350);
    return () => clearTimeout(to);
  }, [stepIdx]);

  const advance = () => {
    setError("");
    setStepIdx((i) => Math.min(i + 1, TOTAL - 1));
  };

  const submit = async () => {
    if (!answers.name.trim()) {
      setError(t("quote.required_name"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers.email)) {
      setError(t("quote.required_email"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/quote-requests`, answers);
      setDone(true);
    } catch (e) {
      setError(t("quote.network_error"));
      setSubmitting(false);
    }
  };

  const handleChoice = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(() => advance(), 400);
  };

  if (done) {
    return (
      <div className="quote-shell" data-testid="quote-done-screen">
        <SEO page="quote" />
        <div className="quote-topbar">
          <img src={LOGO_URL} alt="Be Oasis" className="quote-logo" />
          <LanguageSwitcher />
        </div>
        <div className="quote-done">
          <div className="quote-done-inner">
            <div className="quote-check">
              <Check strokeWidth={2.5} />
            </div>
            <h1 className="quote-done-title" data-testid="quote-done-title">
              {t("quote.done_title1")}{" "}
              <em>{t("quote.done_title_em")}</em>
            </h1>
            <p className="quote-done-body">
              {t("quote.done_body_1")}{" "}
              <strong style={{ color: "#fff" }}>
                {t("quote.done_body_bold")}
              </strong>{" "}
              {t("quote.done_body_2")}
            </p>
            <Link to={homeHref} className="quote-back-home" data-testid="quote-back-home">
              {t("quote.back_home")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isChoice = meta.type === "choice";
  const isText = meta.type === "text";
  const isContact = meta.type === "contact";

  return (
    <div className="quote-shell" data-testid="quote-form">
      <SEO page="quote" />
      <div className="quote-topbar">
        <img
          src={LOGO_URL}
          alt="Be Oasis"
          className="quote-logo"
          draggable="false"
        />
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            className="quote-close"
            onClick={() => navigate(homeHref)}
            aria-label={t("quote.close")}
            data-testid="quote-close"
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>
      </div>

      <div className="quote-stage">
        <div className="quote-stage-inner">
          <div className="quote-step" key={stepIdx}>
            <p className="quote-eyebrow">{step.eyebrow}</p>
            <div className="quote-step-head">
              <span className="quote-badge" data-testid={`quote-badge-${stepIdx + 1}`}>
                {String(stepIdx + 1).padStart(2, "0")}
              </span>
              <span className="quote-badge-arrow">›</span>
              <h2 className="quote-question" data-testid="quote-question">
                {step.q1}
                {step.q_em ? (
                  <>
                    {" "}<em>{step.q_em}</em>
                  </>
                ) : null}
                {step.q2 ? <> {step.q2}</> : null}
              </h2>
            </div>

            {isChoice && Array.isArray(step.opts) && (
              <div className="quote-options">
                {step.opts.map((label, idx) => {
                  const letter = LETTERS[idx];
                  return (
                    <button
                      key={label}
                      type="button"
                      className={`quote-option ${
                        answers[meta.key] === label ? "is-selected" : ""
                      }`}
                      onClick={() => handleChoice(meta.key, label)}
                      data-testid={`quote-option-${meta.key}-${letter}`}
                    >
                      <span className="quote-option-letter">{letter}</span>
                      <span className="quote-option-label">{label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {isText && (
              <div className="quote-input-wrap">
                <input
                  ref={inputRef}
                  className="quote-input"
                  type="text"
                  value={answers[meta.key]}
                  onChange={(e) =>
                    setAnswers((a) => ({ ...a, [meta.key]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") advance();
                  }}
                  placeholder={step.placeholder}
                  data-testid={`quote-input-${meta.key}`}
                />
                <div className="quote-input-row">
                  <button
                    type="button"
                    className="quote-ok"
                    onClick={advance}
                    data-testid="quote-text-ok"
                  >
                    {t("quote.ok")} <span className="quote-ok-check">✓</span>
                  </button>
                  <span className="quote-enter-hint">
                    {t("quote.step_hint_opt")}
                  </span>
                </div>
              </div>
            )}

            {isContact && (
              <div className="quote-input-wrap">
                <label
                  htmlFor="q-name"
                  style={{
                    display: "block",
                    fontFamily: "Space Mono, monospace",
                    fontSize: "0.7rem",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "#D4AE4E",
                    marginBottom: "0.5rem",
                  }}
                >
                  {step.name_label}
                </label>
                <input
                  id="q-name"
                  ref={inputRef}
                  className="quote-input"
                  type="text"
                  value={answers.name}
                  onChange={(e) =>
                    setAnswers((a) => ({ ...a, name: e.target.value }))
                  }
                  placeholder={step.name_ph}
                  data-testid="quote-input-name"
                  required
                />

                <label
                  htmlFor="q-email"
                  style={{
                    display: "block",
                    marginTop: "2rem",
                    fontFamily: "Space Mono, monospace",
                    fontSize: "0.7rem",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "#D4AE4E",
                    marginBottom: "0.5rem",
                  }}
                >
                  {step.email_label}
                </label>
                <input
                  id="q-email"
                  className="quote-input"
                  type="email"
                  value={answers.email}
                  onChange={(e) =>
                    setAnswers((a) => ({ ...a, email: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submit();
                  }}
                  placeholder={step.email_ph}
                  data-testid="quote-input-email"
                  required
                />

                {error && (
                  <p className="quote-error" data-testid="quote-error">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  className="quote-cta-final"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="quote-submit"
                >
                  {submitting ? (
                    <>
                      <span className="quote-loading" />
                      {t("quote.submitting")}
                    </>
                  ) : (
                    <>{t("quote.submit")}</>
                  )}
                </button>
              </div>
            )}

            {error && !isContact && (
              <p className="quote-error">{error}</p>
            )}
          </div>
        </div>
      </div>

      <div className="quote-progress" data-testid="quote-progress">
        {String(stepIdx + 1).padStart(2, "0")} {t("quote.progress_of")}{" "}
        {String(TOTAL).padStart(2, "0")}
      </div>
    </div>
  );
}
